import React, { useCallback, useMemo, useEffect } from 'react';
import { ResponseErrorPanel, TableColumn } from '@backstage/core-components';
import { CopyableText } from '../../CopyableText';
import { ModernResourceView } from '../../ModernResourceView';
import { AwsActionMenu, AwsActionConfirmationDialog } from '../actions';
import { Ec2TableData } from './types';
import { mapResourcesToEc2TableData } from './utils';
import {
  CRDAWSConfig,
  Ec2ResourceDetails,
} from '@internal/backstage-plugin-compute-resource-discovery-new-common';
import { useApi, errorApiRef, alertApiRef } from '@backstage/core-plugin-api';
import { useActionManagement } from './useActionManagement';
import { ExecuteActionOptions } from '../../../api';
import { useAwsContext } from '../AwsContext';
import useAsyncRetry from 'react-use/lib/useAsyncRetry';

interface Ec2TableProps {
  entityRef: string;
  awsConfig: CRDAWSConfig;
  title: string;
}

export const Ec2Table: React.FC<Ec2TableProps> = ({
  entityRef,
  awsConfig,
  title,
}) => {
  const { api, projectCode, refreshContext } = useAwsContext();

  const executeAction = useCallback(
    (options: ExecuteActionOptions) => {
      return api.executeAction(options);
    },
    [api],
  );

  const {
    value: resources,
    loading: isLoading,
    error: error,
    retry: refresh,
  } = useAsyncRetry(async () => {
    if (!projectCode) return [];
    return api.listResources({
      serviceType: 'aws',
      resourceType: 'ec2',
      projectCode: projectCode,
      entityRef: entityRef,
      cloudConfig: awsConfig,
    });
  }, [projectCode, api]);

  const handleRefresh = () => {
    // Invalidate cache for this specific AWS EC2 configuration
    api.invalidateCache('listResources');
    refresh();
  };

  const errorApi = useApi(errorApiRef);
  const alertApi = useApi(alertApiRef);

  const {
    menuPosition,
    confirmationDialogOpen,
    selectedAction,
    isActionInProgress,
    handleTableActionClick,
    handleMenuClose,
    handleActionClick,
    handleConfirmAction,
    setConfirmationDialogOpen,
  } = useActionManagement({
    executeAction,
    refresh,
    errorApi,
    alertApi,
    awsConfig,
    entityRef,
    projectCode,
  });

  useEffect(() => {
    if (!refreshContext) return;

    const refreshKey = `aws:ec2:${awsConfig.accountId}:${awsConfig.region}`;
    const unregister = refreshContext.registerRefresh(refreshKey, refresh);

    return unregister;
  }, [refreshContext, refresh, awsConfig.accountId, awsConfig.region]);

  // Move useMemo hooks before conditional returns
  // Columns for EC2 table view
  const columns = useMemo<TableColumn<Ec2TableData>[]>(
    () => [
      {
        title: 'ID',
        field: 'identifier',
        render: (row: Ec2TableData) => (
          <div style={{ maxWidth: '400px' }}>
            <CopyableText value={row.identifier} />
          </div>
        ),
      },
      {
        title: 'Name',
        field: 'name',
      },
      {
        title: 'Type',
        field: 'type',
      },
      {
        title: 'Status',
        field: 'state',
      },
    ],
    [],
  );

  const data = useMemo(
    () =>
      resources
        ? mapResourcesToEc2TableData(resources as Ec2ResourceDetails[])
        : [],
    [resources],
  );

  if (error) {
    error.message = `Failed to load AWS EC2s`;
    return <ResponseErrorPanel error={error} title={error.message} />;
  }

  return (
    <>
      <ModernResourceView<Ec2TableData>
        title={title}
        data={data}
        isLoading={isLoading}
        onRefresh={handleRefresh}
        onActions={handleTableActionClick}
        cloudService="aws"
        resourceType="ec2"
        columns={columns}
        colorTheme="#FF9900" // AWS Orange
        emptyMessage="No EC2 instances found"
      />

      <AwsActionMenu
        handleActionClick={handleActionClick}
        handleMenuClose={handleMenuClose}
        handleOpenConnection={() => {}} // EC2 doesn't need DB connection
        menuPosition={menuPosition}
        service="ec2"
      />

      <AwsActionConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={handleConfirmAction}
        action={selectedAction as any}
        isLoading={isActionInProgress}
        service="ec2"
      />
    </>
  );
};
